/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B010012 セット構成品入力
 */
import { Column } from "@material-table/core";
import { Box } from "@mui/system";
import Big from "big.js";
import {
  B000111meisaikoseiBiko,
  B000190SokoNmSokoshubetsuKbn,
  B010010Kanosu,
  B010012HandleChangeShohinCd,
  B010012Shohin,
  B010012WhTanka,
  B010012ShohinZaiko,
} from "clients/rest";
import {
  msgState,
  SCancelButton,
  SEntryTab,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  SText,
  STextField,
} from "components";
import { getClientError } from "messages";
import { getSCode, HyoziKazeiKbn } from "messages/codes";
import { labels } from "messages/labels";
import { B000111, B000220 } from "pages";
import { B000190 } from "pages/b000/B000190";
import { B000200 } from "pages/b000/B000200";
import { createRef, useContext, useEffect, useState } from "react";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
  digit,
  getHitsuyosu,
  getJuchuKin,
  getJuchutanka,
  mapIds,
} from "services";
import {
  editKbn,
  setshohinKbn,
  shohinShokuchiKbn,
  shohinshubetsuKbn,
  sokoshubetsuKbn,
  tankaKbn,
  tenyuryokujodaiKbn,
  tenyuryokujuchuKbn,
} from "services/codes";
import { focusState, SDialogContext, useFocus } from "states";

// 呼び出し時のプロパティ
export type B010012Props = Partial<{
  editKbn: string;
  fields: Fields;
  data: Data[];
  meiMethods: UseFormReturn;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  setshohinCd: string;
  setshohinNm: string;
  juchusu: string;
  ikkatsukakeritsu: string;
  setjodaitanka: string;
  setjuchutanka: string;
  zeiRitsu: string;
  denpyoYmd: string | null;
  tokuiCd: string;
  tokuishitenCd: string;
  zeimarumeKbn: number;
  kinmarumeKbn: number;
  tankamarumeKbn: string;
  tankahasuichi: string;
  zeiKbn: string;
  tankaKakeritsu: string;
  jodaihyojiKbn: string;
  isJuchuWork: boolean;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  setshohinCd: "",
  setshohinNm: "",
  juchusu: "",
  ikkatsukakeritsu: "",
  setjodaitanka: "",
  setjuchutanka: "",
  zeiRitsu: "",
  denpyoYmd: "",
  tokuiCd: "",
  tokuishitenCd: "",
  zeimarumeKbn: undefined,
  kinmarumeKbn: undefined,
  tankamarumeKbn: "",
  tankahasuichi: "",
  zeiKbn: "",
  tankaKakeritsu: "",
  jodaihyojiKbn: "",
  isJuchuWork: false,
};

type Data = Partial<{
  juchukoseigyoNo: number;
  shohinCd: string;
  shohinCdBefore: string;
  janCd: string;
  shohinNm: string;
  aiteCd: string;
  sokoCd: string;
  sokoNm: string;
  vol: string;
  sotosunView: string;
  uchisunView: string;
  sotosunW: string;
  sotosunH: string;
  uchisunW: string;
  uchisunH: string;
  koseisu: string;
  hitsuyosu: string;
  hitsuyosuBefore: string;
  kanosu: string;
  tenyuryokujodaiKbn: number;
  tenyuryokujuchuKbn: number;
  basejodaitanka: string;
  basejuchutanka: string;
  jodaitanka: string;
  juchutanka: string;
  kakeritsu: string;
  juchuKin: string;
  meisaikoseiBikoCd: string;
  meisaikoseiBiko: string;
  kazeiKbn: number;
  shohinshubetsuKbn: number;
  tankaKbn: number;
  shokuchiKbn: number;
  whtankaCd: string;
  sokoshubetsuKbn: number;
  setshohinKbn: number;
  zairyohikiatesuBefore: string;
  zairyohikiateKanoNagasa: string;
  zairyohikiateNagasa: string;
  crtDate: string;
  crtshainCd: string;
}>;

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "juchukoseigyoNo", title: "行", width: "2rem" },
  { field: "shohinCd", title: "商品コード", width: "7rem" },
  { field: "shohinCdBefore", title: "商品コード(変更前)", hidden: true },
  { field: "aiteCd", title: "相手コード", width: "7rem" },
  { field: "shohinNm", title: "商品名", width: "20rem" },
  { field: "vol", title: "VOL", width: "4rem" },
  {
    field: "sotosunView",
    title: "WH(外)",
    width: "7rem",
    render: (data) => {
      if (data.sotosunW && Number(data.sotosunW) > 0) {
        return data.sotosunW + " x " + data.sotosunH;
      }
    },
  },
  {
    field: "uchisunView",
    title: "WH(内)",
    width: "7rem",
    render: (data) => {
      if (data.uchisunW && Number(data.uchisunW) > 0) {
        return data.uchisunW + " x " + data.uchisunH;
      }
    },
  },
  { field: "sotosunW", title: "外幅", hidden: true },
  { field: "sotosunH", title: "外高さ", hidden: true },
  { field: "uchisunW", title: "内幅", hidden: true },
  { field: "uchisunH", title: "内高さ", hidden: true },
  {
    field: "koseisu",
    title: "構成数",
    width: "5rem",
    align: "right",
    render: (data) => digit(data.koseisu),
  },
  {
    field: "hitsuyosu",
    title: "必要数",
    width: "5rem",
    align: "right",
    render: (data) => digit(data.hitsuyosu),
  },
  { field: "hitsuyosuBefore", title: "必要数(変更前)", hidden: true },
  {
    field: "kanosu",
    title: "可能数",
    width: "5rem",
    align: "right",
    render: (data) => digit(data.kanosu),
  },
  {
    field: "jodaitanka",
    title: "上代",
    width: "5rem",
    align: "right",
    render: (data) => {
      let backgroundColor = "inherit";
      if (data.tenyuryokujodaiKbn === tenyuryokujodaiKbn.exists) {
        backgroundColor = "#FFFF00";
      }
      return <Box sx={{ backgroundColor }}>{digit(data.jodaitanka)}</Box>;
    },
  },
  {
    field: "juchutanka",
    title: "下代",
    width: "5rem",
    align: "right",
    render: (data) => {
      let backgroundColor = "inherit";
      if (data.tenyuryokujuchuKbn === tenyuryokujuchuKbn.exists) {
        backgroundColor = "#FFFF00";
      }
      return <Box sx={{ backgroundColor }}>{digit(data.juchutanka)}</Box>;
    },
  },
  { field: "kakeritsu", title: "掛率", width: "6rem", align: "right" },
  {
    field: "juchuKin",
    title: "受注金額",
    width: "5rem",
    align: "right",
    render: (data) => digit(data.juchuKin),
  },
  { field: "sokoCd", title: "倉庫", width: "4rem" },
  { field: "sokoNm", title: "", width: "6rem" },
  { field: "meisaikoseiBikoCd", title: "構成明細備考", width: "5rem" },
  { field: "meisaikoseiBiko", title: "", width: "8rem" },
  {
    field: "kazeiKbnView",
    title: "税",
    width: "4rem",
    render: (data) =>
      getSCode(HyoziKazeiKbn, data.kazeiKbn?.toString() ?? "")?.label,
  },
  { field: "kazeiKbn", title: "税", width: "6rem", hidden: true },
  { field: "janCd", title: "JAN コード", width: "5rem", hidden: true },
  { field: "tenyuryokujodaiKbn", hidden: true },
  { field: "tenyuryokujuchuKbn", hidden: true },
  { field: "basejodaitanka", hidden: true },
  { field: "basejuchutanka", hidden: true },
  { field: "shohinshubetsuKbn", hidden: true },
  { field: "tankaKbn", hidden: true },
  { field: "shokuchiKbn", hidden: true },
  { field: "whtankaCd", hidden: true },
  { field: "setshohinKbn", hidden: true },
  { field: "sokoshubetsuKbn", hidden: true },
  { field: "zairyohikiatesuBefore", hidden: true },
  { field: "zairyohikiateKanoNagasa", hidden: true },
  { field: "zairyohikiateNagasa", hidden: true },
  { field: "crtDate", hidden: true },
  { field: "crtshainCd", hidden: true },
];

//明細入力部
type MeiFields = Partial<{
  meiTab: boolean;
  juchukoseigyoNo: number;
  shohinCd: string;
  shohinCdBefore: string;
  janCd: string;
  shohinNm: string;
  sokoCd: string;
  sokoNm: string;
  aiteCd: string;
  sotosunW: string;
  sotosunH: string;
  uchisunW: string;
  uchisunH: string;
  hitsuyosu: string;
  hitsuyosuBefore: string;
  koseisu: string;
  vol: string;
  kanosu: string;
  tenyuryokujodaiKbn: number;
  tenyuryokujuchuKbn: number;
  basejodaitanka: string;
  basejuchutanka: string;
  jodaitanka: string;
  juchutanka: string;
  kakeritsu: string;
  juchuKin: string;
  meisaikoseiBikoCd: string;
  meisaikoseiBikoBefore: string;
  meisaikoseiBiko: string;
  kazeiKbn: number;
  shohinshubetsuKbn: number;
  tankaKbn: number;
  shokuchiKbn: number;
  whtankaCd: string;
  setshohinKbn: number;
  sokoshubetsuKbn: number;
  zairyohikiatesuBefore: string;
  zairyohikiateKanoNagasa: string;
  zairyohikiateNagasa: string;
  crtDate: string;
  crtshainCd: string;
  dirtyFields: {
    shohinCd: string;
    sokoCd: string;
    sotosunW: string;
    sotosunH: string;
    koseisu: string;
    jodaitanka: string;
    juchutanka: string;
    kakeritsu: string;
  };
}>;

const defaultMeiFields: MeiFields = {
  meiTab: true,
  juchukoseigyoNo: undefined,
  shohinCd: "",
  shohinCdBefore: "",
  janCd: "",
  sokoCd: "",
  sokoNm: "",
  shohinNm: "",
  aiteCd: "",
  sotosunW: "",
  sotosunH: "",
  uchisunW: "",
  uchisunH: "",
  hitsuyosu: "",
  hitsuyosuBefore: "",
  koseisu: "",
  vol: "",
  kanosu: "",
  jodaitanka: "",
  juchutanka: "",
  kakeritsu: "",
  juchuKin: "",
  meisaikoseiBikoCd: "",
  meisaikoseiBikoBefore: "",
  meisaikoseiBiko: "",
  kazeiKbn: undefined,
  tenyuryokujodaiKbn: undefined,
  tenyuryokujuchuKbn: undefined,
  basejodaitanka: "",
  basejuchutanka: "",
  shohinshubetsuKbn: undefined,
  tankaKbn: undefined,
  shokuchiKbn: undefined,
  whtankaCd: "",
  setshohinKbn: undefined,
  sokoshubetsuKbn: undefined,
  zairyohikiatesuBefore: "",
  zairyohikiateKanoNagasa: "",
  zairyohikiateNagasa: "",
  crtDate: "",
  crtshainCd: "",
  dirtyFields: {
    shohinCd: "",
    sokoCd: "",
    sotosunW: "",
    sotosunH: "",
    koseisu: "",
    jodaitanka: "",
    juchutanka: "",
    kakeritsu: "",
  },
};

// 固定値
const juchusuMin = 1;
const whMin = 1;
const jodaitankaMaxLength = 9;
const juchutankaMaxLength = 9;

const focusId = "B010012";

const titleEdit = "セット構成品入力";
const titleReference = "セット構成品照会";

export const B010012 = (props: B010012Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};
const Page = (props: B010012Props) => {
  // 初期設定
  // テーブル管理
  const tableRef = createRef();
  const [rows, setRows] = useState<Data[]>([]);
  const [rowCount, setRowCount] = useState(0);
  // テーブル行で選択されたデータ
  const [rowData, setRowData] = useState<Partial<Data>>();
  // ダイアログで処理を行う関数
  const handleSubmit = useContext(SDialogContext);

  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });

  const meiMethods = useForm<MeiFields>({
    mode: "onBlur",
    defaultValues: defaultMeiFields,
  });

  // dirtyFields初期化
  const resetMeiDirtyFields = () => {
    const fields = meiMethods.getValues();
    meiMethods.setValue("dirtyFields.shohinCd", fields.shohinCd ?? "");
    meiMethods.setValue("dirtyFields.sokoCd", fields.sokoCd ?? "");
    meiMethods.setValue("dirtyFields.sotosunW", fields.sotosunW ?? "");
    meiMethods.setValue("dirtyFields.sotosunH", fields.sotosunH ?? "");
    meiMethods.setValue("dirtyFields.koseisu", fields.koseisu ?? "");
    meiMethods.setValue("dirtyFields.jodaitanka", fields.jodaitanka ?? "");
    meiMethods.setValue("dirtyFields.juchutanka", fields.juchutanka ?? "");
    meiMethods.setValue("dirtyFields.kakeritsu", fields.kakeritsu ?? "");
  };

  // タイトル設定(初期値は「セット構成品入力」)
  const [title, setTitle] = useState(titleEdit);

  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // 遷移元データ取得用
  const navigate = useNavigate();

  // 参照モード設定
  const [isReference, setIsReference] = useState(true);
  // エラー表示
  const setMsg = useSetRecoilState(msgState);

  // 再レンダリング用フラグ
  const [reRender, setReRender] = useState(false);
  const reRendering = () => {
    resetMeiDirtyFields();
    setReRender(!reRender);
  };

  // ベース上代単価、上代単価が一致するかどうか(手入力上代区分)
  const isTenyuryokujodaiKbn = () =>
    Number(meiMethods.getValues().basejodaitanka) ===
    Number(meiMethods.getValues().jodaitanka);
  // ベース下代単価、下代単価が一致するかどうか(手入力受注区分)
  const isTenyuryokujuchuKbn = () =>
    Number(meiMethods.getValues().basejuchutanka) ===
    Number(meiMethods.getValues().juchutanka);
  // 商品.諸口区分が「1:諸口」かどうか
  const isShohinShokuchiKbn = () =>
    meiMethods.getValues().shokuchiKbn === shohinShokuchiKbn.shokuchi;
  // 明細入力部の金額の入力可否(構成数が入力済、かつ下代が入力済の場合、入力不可)
  const isKingakuDisabled = () =>
    meiMethods.getValues().koseisu !== "" &&
    meiMethods.getValues().juchutanka !== "";
  // 商品.商品種別区分が「4:受注生産品」かどうか
  const isMeiJuchuSeisanhin = () =>
    meiMethods.getValues().shohinshubetsuKbn === shohinshubetsuKbn.madeToOrder;
  // 商品.セット商品区分が「1:セット品」かどうか
  const isSetshohinKbn = () =>
    meiMethods.getValues().setshohinKbn === setshohinKbn.set;

  useEffect(() => {
    // 編集区分の設定
    switch (props.editKbn) {
      case editKbn.register:
        setIsReference(false);
        break;
      case editKbn.update:
        setIsReference(false);
        break;
      case editKbn.reference:
        setIsReference(true);
        // 参照時はタイトルを変更
        setTitle(titleReference);
        break;
      default:
        setIsReference(true);
        break;
    }
    // 入力プロパティの反映
    methods.setValue("setshohinCd", props.fields?.setshohinCd);
    methods.setValue("setshohinNm", props.fields?.setshohinNm);
    methods.setValue("juchusu", props.fields?.juchusu);
    methods.setValue("zeiRitsu", props.fields?.zeiRitsu);
    methods.setValue("denpyoYmd", props.fields?.denpyoYmd);
    methods.setValue("tokuiCd", props.fields?.tokuiCd);
    methods.setValue("tokuishitenCd", props.fields?.tokuishitenCd);
    methods.setValue("zeimarumeKbn", props.fields?.zeimarumeKbn);
    methods.setValue("kinmarumeKbn", props.fields?.kinmarumeKbn);
    methods.setValue("tankamarumeKbn", props.fields?.tankamarumeKbn);
    methods.setValue("tankahasuichi", props.fields?.tankahasuichi);
    methods.setValue("zeiKbn", props.fields?.zeiKbn);
    methods.setValue("tankaKakeritsu", props.fields?.tankaKakeritsu);
    methods.setValue("jodaihyojiKbn", props.fields?.jodaihyojiKbn);
    // ディープコピーするため、mapとスプレッド構文で設定
    setRows(props.data ? props.data.map((list) => ({ ...list })) : []);
    methods.setValue("isJuchuWork", props.fields?.isJuchuWork);
    reRendering();
  }, []);

  // 明細変更
  useEffect(() => {
    //合計金額算出
    handleGokeiCalculating();
    reRendering();
  }, [rows]);

  const b010012Props: B010012Props = {
    editKbn: editKbn.update,
    fields: {},
    data: [],
    meiMethods: meiMethods,
  };
  // 登録ボタンクリックイベント
  const handleClickCreateButton = () => {
    const fields = methods.getValues();
    b010012Props.fields = {
      juchusu: fields.juchusu,
      setjodaitanka: fields.setjodaitanka,
      setjuchutanka: fields.setjuchutanka,
    };
    b010012Props.data = rows?.map((value) => ({
      juchukoseigyoNo: value.juchukoseigyoNo,
      shohinCd: value.shohinCd,
      shohinCdBefore: value.shohinCdBefore,
      janCd: value.janCd,
      shohinNm: value.shohinNm,
      aiteCd: value.aiteCd,
      sokoCd: value.sokoCd,
      sokoNm: value.sokoNm,
      vol: value.vol,
      sotosunW: value.sotosunW,
      sotosunH: value.sotosunH,
      uchisunW: value.uchisunW,
      uchisunH: value.uchisunH,
      hitsuyosu: value.hitsuyosu,
      hitsuyosuBefore: value.hitsuyosuBefore,
      koseisu: value.koseisu,
      kanosu: value.kanosu,
      tenyuryokujodaiKbn: value.tenyuryokujodaiKbn,
      tenyuryokujuchuKbn: value.tenyuryokujuchuKbn,
      basejodaitanka: value.jodaitanka,
      basejuchutanka: value.juchutanka,
      jodaitanka: value.jodaitanka,
      juchutanka: value.juchutanka,
      kakeritsu: value.kakeritsu,
      juchuKin: value.juchuKin,
      meisaikoseiBikoCd: value.meisaikoseiBikoCd,
      meisaikoseiBiko: value.meisaikoseiBiko,
      kazeiKbn: value.kazeiKbn,
      shohinshubetsuKbn: value.shohinshubetsuKbn,
      tankaKbn: value.tankaKbn,
      shokuchiKbn: value.shokuchiKbn,
      whtankaCd: value.whtankaCd,
      setshohinKbn: value.setshohinKbn,
      sokoshubetsuKbn: value.sokoshubetsuKbn,
      zairyohikiatesuBefore: value.zairyohikiatesuBefore,
      zairyohikiateKanoNagasa: value.zairyohikiateKanoNagasa,
      zairyohikiateNagasa: value.zairyohikiateNagasa,
      crtDate: value.crtDate,
      crtshainCd: value.crtshainCd,
    }));

    // 変更されたデータ内容を受注入力画面へ
    handleSubmit(b010012Props);
  };

  // 一括掛率の計算
  const handleClickCalculateButton = () => {
    // フォームのデータを取得する。
    const fields = methods.getValues();

    // 一括掛率が未入力の場合
    if (fields.ikkatsukakeritsu === "") {
      // 何もしない
      return;
    }
    // 明細が設定されていない場合
    if (rows.length === 0) {
      // 何もしない
      return;
    }

    // 再計算を行う
    const newRows = rows.map((meiRow: Data) => {
      // 掛率の再設定
      meiRow.kakeritsu = fields.ikkatsukakeritsu;
      // 下代単価
      meiRow.juchutanka = getJuchutanka(
        methods,
        meiRow.jodaitanka!,
        meiRow.kakeritsu!
      );
      meiRow.basejuchutanka = meiRow.juchutanka;
      // 受注金額
      meiRow.juchuKin = getJuchuKin(
        meiRow.juchutanka,
        meiRow.hitsuyosu!,
        fields.kinmarumeKbn!
      );
      return meiRow;
    });

    setRows(newRows);
  };

  // 受注数の変更
  const handleChangeJuhusu = () => {
    // フォームのデータを取得する。
    const fields = methods.getValues();
    const meiFields = meiMethods.getValues();

    // 構成数が未入力の場合、必要数、受注金額は再計算しない
    if (meiFields.koseisu !== "") {
      // 明細入力部の変更
      // 必要数を設定
      meiMethods.setValue(
        "hitsuyosu",
        getHitsuyosu(meiFields.koseisu!, fields.juchusu!)
      );

      // 受注金額
      handleCalcJuchuKin();
      reRendering();
    }

    // 明細が設定されていない場合、明細の再計算は行わない
    if (rows.length !== 0) {
      // 再計算を行う
      const newRows = rows.map((meiRow: Data) => {
        // 必要数
        meiRow.hitsuyosu = getHitsuyosu(meiRow.koseisu!, fields.juchusu!);
        // 受注金額
        meiRow.juchuKin = getJuchuKin(
          meiRow.juchutanka!,
          meiRow.hitsuyosu!,
          fields.kinmarumeKbn!
        );
        return meiRow;
      });

      setRows(newRows);
    }
  };

  // 下代、受注金額設定
  const handleCalcJuchutankaJuchuKin = () => {
    // フォームのデータを取得する。
    const meiFields = meiMethods.getValues();

    if (
      meiFields.kakeritsu === meiFields.dirtyFields?.kakeritsu &&
      meiFields.jodaitanka === meiFields.dirtyFields?.jodaitanka
    ) {
      return;
    }

    // 上代単価、掛率が未入力の場合
    if (meiFields.jodaitanka === "") {
      return;
    }
    if (meiFields.kakeritsu === "") {
      return;
    }

    // 手入力受注区分が「1:有り」の場合
    if (!isTenyuryokujuchuKbn()) {
      return;
    }

    // 下代単価取得
    const juchutanka: string = getJuchutanka(
      methods,
      meiFields.jodaitanka!,
      meiFields.kakeritsu!
    );
    // 下代単価
    meiMethods.setValue("juchutanka", juchutanka);
    // ベース下代単価
    meiMethods.setValue("basejuchutanka", juchutanka);

    // 受注金額
    handleCalcJuchuKin();
    reRendering();
  };

  // 構成数変更
  const handleChangeKoseisu = () => {
    // フォームのデータを取得する。
    const fields = methods.getValues();
    const meiFields = meiMethods.getValues();

    // 受注数が入力されている場合
    if (fields.juchusu !== "") {
      // 必要数を設定
      meiMethods.setValue(
        "hitsuyosu",
        getHitsuyosu(meiFields.koseisu!, fields.juchusu!)
      );
    }

    // 受注金額
    handleCalcJuchuKin();
    reRendering();
  };

  // 下代入力時設定
  const handleCalcJuchuKinFromJuchutanka = () => {
    const meiFields = meiMethods.getValues();
    if (meiFields.juchutanka === meiFields.dirtyFields?.juchutanka) return;
    handleCalcJuchuKin();
    reRendering();
  };

  // 受注金額設定
  const handleCalcJuchuKin = () => {
    // フォームのデータを取得する。
    const fields = methods.getValues();
    const meiFields = meiMethods.getValues();
    // 受注金額
    meiMethods.setValue(
      "juchuKin",
      getJuchuKin(
        meiFields.juchutanka!,
        meiFields.hitsuyosu!,
        fields.kinmarumeKbn!
      )
    );
  };

  // 商品コード変更
  const handleChangeShohinCd = () => {
    (async () => {
      const meiFields = meiMethods.getValues();
      if (meiFields.shohinCd === meiFields.dirtyFields?.shohinCd) return;
      await B010012HandleChangeShohinCd(methods, meiMethods, navigate, setMsg);
      reRendering();
    })();
  };

  // 倉庫コード変更
  const handleChangeSokoCd = () => {
    (async () => {
      const meiFields = meiMethods.getValues();
      if (meiFields.sokoCd === meiFields.dirtyFields?.sokoCd) return;
      // 倉庫名表示
      await B000190SokoNmSokoshubetsuKbn(meiMethods, navigate, setMsg);
      // 引当可能数取得処理
      await B010010Kanosu(meiMethods, navigate, setMsg);
      reRendering();
    })();
  };

  // WH単価変更
  const handleChangeWhTanka = () => {
    (async () => {
      const meiFields = meiMethods.getValues();
      if (
        meiFields.sotosunW === meiFields.dirtyFields?.sotosunW &&
        meiFields.sotosunH === meiFields.dirtyFields?.sotosunH
      ) {
        return;
      }
      await B010012WhTanka(methods, meiMethods, navigate, setMsg);
      reRendering();
    })();
  };

  // タブ切替
  const handleChangeMeiTab = () => {
    if (!meiMethods.watch("meiTab")) return;
    meiMethods.reset();
    meiMethods.reset({
      ...meiMethods.getValues(),
      shohinCd: "",
      janCd: "",
    });
    meiMethods.setValue("meiTab", true);
    reRendering();
  };

  // 明細登録
  const handleClickCreateMeiButton = () => {
    // 独自チェック
    if (invalidated()) return;

    // // フィールドチェック
    meiMethods.handleSubmit(
      () => {
        // 明細に設定するデータを取得
        const juchukoseigyoNo: number = rows.length + 1;
        const data: Data = handleDataMapping(juchukoseigyoNo);

        // 明細を設定
        setRows([...rows, data]);
        setRowCount(juchukoseigyoNo);

        // 行選択の解除
        setRowData(undefined);

        // 明細クリア
        meiMethods.reset();
        meiMethods.reset({
          ...meiMethods.getValues(),
          shohinCd: "",
          janCd: "",
        });

        // 明細入力部を新規登録に変更する(明示的にtrueに設定しないと削除、更新ボタンが表示される)
        meiMethods.setValue("meiTab", true);

        focus(4);
        reRendering();
      },
      () => {
        focus(4);
        reRendering();
      }
    )();
  };

  // 明細更新
  const handleClickUpdateMeiButton = () => {
    // 独自チェック
    if (invalidated()) return;

    // フィールドチェック
    meiMethods.handleSubmit(
      () => {
        const juchukoseigyoNo: number = rowData!.juchukoseigyoNo!;
        // 明細に設定するデータを取得
        const data: Data = handleDataMapping(juchukoseigyoNo);
        // 明細を設定
        rows[juchukoseigyoNo - 1] = data;
        setRows([...rows]);

        // 行選択の解除
        setRowData(undefined);

        // 明細クリア
        meiMethods.reset();
        meiMethods.reset({
          ...meiMethods.getValues(),
          shohinCd: "",
          janCd: "",
        });

        // 明細入力部を新規登録に変更する
        meiMethods.setValue("meiTab", true);
        focus(4);
        reRendering();
      },
      () => {
        focus(4);
        reRendering();
      }
    )();
  };

  // 独自チェック
  const invalidated = (): boolean => {
    // フォームのデータを取得する。
    const fields = methods.getValues();
    const meiFields = meiMethods.getValues();
    // 行番号
    const juchukoseigyoNo: number = rowData?.juchukoseigyoNo ?? rows.length + 1;

    // 受注数が未入力の場合
    if (fields.juchusu === "") {
      setMsg(getClientError("B010012_001"));
      return true;
    }

    // 受注数が1より小さい場合
    if (Number(fields.juchusu) < juchusuMin) {
      setMsg(getClientError("B010012_001"));
      return true;
    }

    // 商品種別区分が「9:材料品」の場合
    if (meiFields.shohinshubetsuKbn === shohinshubetsuKbn.materials) {
      setMsg(getClientError("B010012_002"));
      return true;
    }

    // 倉庫が物流倉庫、引当倉庫以外の場合
    if (
      !(
        meiFields.sokoshubetsuKbn === sokoshubetsuKbn.logicalSoko ||
        meiFields.sokoshubetsuKbn === sokoshubetsuKbn.hikiateSoko
      )
    ) {
      setMsg(getClientError("B010012_003"));
      return true;
    }

    // 外寸の入力値チェック
    if (meiFields.sotosunH || meiFields.sotosunW) {
      // 外寸が片方だけ入力されている場合エラー
      if (!meiFields.sotosunW) {
        setMsg(getClientError("B010012_004"));
        return true;
      }
      if (!meiFields.sotosunH) {
        setMsg(getClientError("B010012_005"));
        return true;
      }
    }
    // 内寸の入力値チェック
    if (meiFields.uchisunH || meiFields.uchisunW) {
      // 内寸が片方だけ入力されている場合エラー
      if (!meiFields.uchisunW) {
        setMsg(getClientError("B010012_008"));
        return true;
      }
      if (!meiFields.uchisunH) {
        setMsg(getClientError("B010012_009"));
        return true;
      }
    }
    // 上代合計・下代合計の金額が9桁より大きい場合エラー
    // 上代合計(現在編集中の行を除いた合計値を取得)
    const jodaitankaGokei: string = rows
      .filter(function (source: Data) {
        return source.juchukoseigyoNo !== juchukoseigyoNo;
      })
      .reduce((acc: Big, row: Data): Big => {
        return acc.plus(new Big(row.jodaitanka ? row.jodaitanka : "0"));
      }, new Big("0"))
      .plus(new Big(meiFields.jodaitanka ? meiFields.jodaitanka : "0"))
      .toString();
    //上代合計が9桁より大きい場合
    if (jodaitankaGokei.length > jodaitankaMaxLength) {
      setMsg(getClientError("B010012_012"));
      return true;
    }

    // 下代合計(現在編集中の行を除いた合計値を取得)
    const juchutankaGokei: string = rows
      .filter(function (source: Data) {
        return source.juchukoseigyoNo !== juchukoseigyoNo;
      })
      .reduce((acc: Big, row: Data): Big => {
        return acc.plus(new Big(row.juchutanka ? row.juchutanka : "0"));
      }, new Big("0"))
      .plus(new Big(meiFields.juchutanka ? meiFields.juchutanka : "0"))
      .toString();
    // 下代合計が9桁より大きい場合
    if (juchutankaGokei.length > juchutankaMaxLength) {
      setMsg(getClientError("B010012_013"));
      return true;
    }

    // セット商品の場合
    if (meiFields.setshohinKbn === setshohinKbn.set) {
      setMsg(getClientError("B010012_014"));
      return true;
    }

    // WH単価の商品の場合
    if (meiFields.tankaKbn === tankaKbn.wh) {
      // 外寸が入っていない場合
      if (!meiFields.sotosunW) {
        setMsg(getClientError("B010012_015"));
        return true;
      }
      if (!meiFields.sotosunH) {
        setMsg(getClientError("B010012_015"));
        return true;
      }
      // 外寸が0以下の場合
      if (Number(meiFields.sotosunW) < whMin) {
        setMsg(getClientError("B010012_006"));
        return true;
      }
      if (Number(meiFields.sotosunH) < whMin) {
        setMsg(getClientError("B010012_007"));
        return true;
      }
    }

    // 呼び出し元が受注ワーク入力の場合は引当可能かチェックしない
    if (fields.isJuchuWork) {
      return false;
    }

    // 可能数が空の場合は引当可能かチェックしない(受注生産品かつ材料商品コードなしのため)
    if (meiFields.kanosu === null || meiFields.kanosu === "") {
      return false;
    }

    // 商品コードが前回分の明細.商品コードと同じ場合
    if (meiFields.shohinCd === meiFields.shohinCdBefore) {
      // 通常単価の商品の場合
      if (meiFields.tankaKbn === tankaKbn.tujo) {
        // 必要数 > 可能数 + 前回分の必要数の場合
        if (
          Number(meiFields.hitsuyosu) >
          Number(meiFields.kanosu) + Number(meiFields.hitsuyosuBefore)
        ) {
          setMsg(getClientError("B010012_016"));
          return true;
        }
      }
      // WH単価の商品の場合
      else {
        // 今回必要な長さを取得
        const hituyoNagasa: Big = new Big(
          meiFields.zairyohikiateNagasa ? meiFields.zairyohikiateNagasa : "0"
        ).mul(new Big(meiFields.hitsuyosu!));

        // 今回必要な長さ > 前回分を考慮した引当可能な長さ
        if (Number(hituyoNagasa) > Number(meiFields.zairyohikiateKanoNagasa)) {
          setMsg(getClientError("B010012_016"));
          return true;
        }
      }
    }
    // 商品コードが前回分の明細.商品コードと同じではない場合
    else {
      // 必要数 > 可能数の場合
      if (Number(meiFields.hitsuyosu) > Number(meiFields.kanosu)) {
        setMsg(getClientError("B010012_016"));
        return true;
      }
    }

    return false;
  };

  // 明細のデータマッピング
  const handleDataMapping = (juchukoseigyoNo: number): Data => {
    // フォームのデータを取得する。
    const meiFields = meiMethods.getValues();
    const data: Data = {
      juchukoseigyoNo,
      shohinCd: meiFields.shohinCd,
      shohinCdBefore: meiFields.shohinCdBefore,
      janCd: meiFields.janCd,
      shohinNm: meiFields.shohinNm,
      aiteCd: meiFields.aiteCd,
      sokoCd: meiFields.sokoCd,
      sokoNm: meiFields.sokoNm,
      vol: meiFields.vol,
      sotosunW: meiFields.sotosunW,
      sotosunH: meiFields.sotosunH,
      uchisunW: meiFields.uchisunW,
      uchisunH: meiFields.uchisunH,
      koseisu: meiFields.koseisu,
      hitsuyosu: meiFields.hitsuyosu,
      hitsuyosuBefore: meiFields.hitsuyosuBefore,
      kanosu: meiFields.kanosu,
      // 手入力上代区分の設定(一致する場合「0：変更なし」、一致する場合「1：変更あり」)
      tenyuryokujodaiKbn: isTenyuryokujodaiKbn()
        ? tenyuryokujodaiKbn.none
        : tenyuryokujodaiKbn.exists,
      // 手入力受注区分の設定(一致する場合「0：変更なし」、一致する場合「1：変更あり」)
      tenyuryokujuchuKbn: isTenyuryokujuchuKbn()
        ? tenyuryokujuchuKbn.none
        : tenyuryokujuchuKbn.exists,
      basejodaitanka: meiFields.basejodaitanka,
      basejuchutanka: meiFields.basejuchutanka,
      jodaitanka: meiFields.jodaitanka,
      juchutanka: meiFields.juchutanka,
      kakeritsu: meiFields.kakeritsu,
      juchuKin: meiFields.juchuKin,
      meisaikoseiBikoCd: meiFields.meisaikoseiBikoCd,
      meisaikoseiBiko: meiFields.meisaikoseiBiko,
      kazeiKbn: meiFields.kazeiKbn,
      shohinshubetsuKbn: meiFields.shohinshubetsuKbn,
      tankaKbn: meiFields.tankaKbn,
      shokuchiKbn: meiFields.shokuchiKbn,
      whtankaCd: meiFields.whtankaCd,
      setshohinKbn: meiFields.setshohinKbn,
      sokoshubetsuKbn: meiFields.sokoshubetsuKbn,
      zairyohikiatesuBefore: meiFields.zairyohikiatesuBefore,
      zairyohikiateKanoNagasa: meiFields.zairyohikiateKanoNagasa,
      zairyohikiateNagasa: meiFields.zairyohikiateNagasa,
      crtDate: meiFields.crtDate,
      crtshainCd: meiFields.crtshainCd,
    };

    return data;
  };

  // 明細削除
  const handleClickDeleteMeiButton = () => {
    // 選択行の取得
    if (typeof rowData?.juchukoseigyoNo === "undefined") return;
    const juchukoseigyoNo: number = rowData.juchukoseigyoNo;
    // 明細の連番の振り直し、対象の行を削除
    const newRows = mapIds(
      rows.filter(function (source: Data) {
        return source.juchukoseigyoNo !== juchukoseigyoNo;
      }),
      "juchukoseigyoNo"
    );

    setRows(newRows);

    // 明細クリア
    meiMethods.reset();
    meiMethods.reset({
      ...meiMethods.getValues(),
      shohinCd: "",
      janCd: "",
    });

    // 明細入力部を新規登録に変更する
    meiMethods.setValue("meiTab", true);
    reRendering();
  };

  //合計金額算出
  const handleGokeiCalculating = () => {
    // 明細が設定されていない場合
    if (rows.length === 0) {
      // 合計金額の初期化のみ行う
      methods.setValue("setjodaitanka", "");
      methods.setValue("setjuchutanka", "");
      return;
    }

    // 上代単価合計(上代 × 構成数)
    const jodaitankaGokei: string = rows
      .reduce((acc: Big, row: Data): Big => {
        return acc.plus(
          new Big(row.jodaitanka ? row.jodaitanka : "0").times(
            new Big(row.koseisu!)
          )
        );
      }, new Big("0"))
      .toString();
    // 受注単価合計(下代 × 構成数)
    const juchutankaGokei: string = rows
      .reduce((acc: Big, row: Data): Big => {
        return acc.plus(
          new Big(row.juchutanka ? row.juchutanka : "0").times(
            new Big(row.koseisu!)
          )
        );
      }, new Big("0"))
      .toString();

    // セット上代単価
    methods.setValue("setjodaitanka", jodaitankaGokei);
    // セット下代単価
    methods.setValue("setjuchutanka", juchutankaGokei);
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    // データを保存
    setRowData(data);
    if (typeof data.juchukoseigyoNo !== "undefined") {
      // 明細入力部を編集に変更する
      meiMethods.setValue("meiTab", false);
      // 明細入力部に値を設定する
      meiMethods.setValue("juchukoseigyoNo", data.juchukoseigyoNo);
      meiMethods.setValue("shohinCd", data.shohinCd);
      meiMethods.setValue("shohinCdBefore", data.shohinCdBefore);
      meiMethods.setValue("janCd", data.janCd);
      meiMethods.setValue("shohinNm", data.shohinNm);
      meiMethods.setValue("aiteCd", data.aiteCd);
      meiMethods.setValue("sokoCd", data.sokoCd);
      meiMethods.setValue("sokoNm", data.sokoNm);
      meiMethods.setValue("vol", data.vol);
      meiMethods.setValue("sotosunW", data.sotosunW);
      meiMethods.setValue("sotosunH", data.sotosunH);
      meiMethods.setValue("uchisunW", data.uchisunW);
      meiMethods.setValue("uchisunH", data.uchisunH);
      meiMethods.setValue("hitsuyosu", data.hitsuyosu);
      meiMethods.setValue("hitsuyosuBefore", data.hitsuyosuBefore);
      meiMethods.setValue("kanosu", data.kanosu);
      meiMethods.setValue("tenyuryokujodaiKbn", data.tenyuryokujodaiKbn);
      meiMethods.setValue("tenyuryokujuchuKbn", data.tenyuryokujuchuKbn);
      meiMethods.setValue("basejodaitanka", data.basejodaitanka);
      meiMethods.setValue("basejuchutanka", data.basejuchutanka);
      meiMethods.setValue("jodaitanka", data.jodaitanka);
      meiMethods.setValue("juchutanka", data.juchutanka);
      meiMethods.setValue("kakeritsu", data.kakeritsu);
      meiMethods.setValue("koseisu", data.koseisu);
      meiMethods.setValue("juchuKin", data.juchuKin);
      meiMethods.setValue("meisaikoseiBikoCd", data.meisaikoseiBikoCd);
      meiMethods.setValue("meisaikoseiBikoBefore", data?.meisaikoseiBikoCd);
      meiMethods.setValue("meisaikoseiBiko", data.meisaikoseiBiko);
      meiMethods.setValue("kazeiKbn", data.kazeiKbn);
      meiMethods.setValue("shohinshubetsuKbn", data.shohinshubetsuKbn);
      meiMethods.setValue("tankaKbn", data.tankaKbn);
      meiMethods.setValue("shokuchiKbn", data.shokuchiKbn);
      meiMethods.setValue("whtankaCd", data.whtankaCd);
      meiMethods.setValue("setshohinKbn", data.setshohinKbn);
      meiMethods.setValue("sokoshubetsuKbn", data.sokoshubetsuKbn);
      meiMethods.setValue("zairyohikiatesuBefore", data.zairyohikiatesuBefore);
      meiMethods.setValue(
        "zairyohikiateKanoNagasa",
        data.zairyohikiateKanoNagasa
      );
      meiMethods.setValue("zairyohikiateNagasa", data.zairyohikiateNagasa);
      meiMethods.setValue("crtDate", data.crtDate);
      meiMethods.setValue("crtshainCd", data.crtshainCd);
      focus(4);
      reRendering();
    }
  };

  // レイアウト設定
  return (
    <>
      <FormProvider {...methods}>
        <SFrame
          title={title}
          gridTemplateColumns="6rem 20rem 8rem 8rem 8rem 4rem 1rem 4rem"
        >
          {!isReference && (
            <>
              <SNormalButton
                label={labels.buttons.create}
                gridColumn="8"
                onClick={handleClickCreateButton}
              />
            </>
          )}
          <SLabel gridColumn={1}>商品コード</SLabel>
          <SNumberField name="setshohinCd" code size={10} disabled />
          <SLabel gridColumn="4">一括掛率</SLabel>
          <SNumberField
            name="ikkatsukakeritsu"
            size={3}
            {...register(2)}
            disabled={isReference}
          />
          <SNormalButton
            label={labels.buttons.calculate}
            {...register(3)}
            onClick={handleClickCalculateButton}
            disabled={isReference}
          />
          <SLabel gridColumn="1">商品名</SLabel>
          <STextField name="setshohinNm" size={40} disabled />
          <SLabel required gridColumn="1">
            受注数
          </SLabel>
          <SNumberField
            label="受注数"
            name="juchusu"
            required
            size={6}
            {...register(1)}
            onBlur={() => handleChangeJuhusu()}
            disabled={isReference}
          />
          <SLabel gridColumn="4">セット上代単価</SLabel>
          <SLabel>セット下代単価</SLabel>
          <SNumberField
            name="setjodaitanka"
            size={9}
            align="right"
            disabled
            gridColumn="4"
          />
          <SNumberField
            name="setjuchutanka"
            size={9}
            align="right"
            disabled
            gridColumn="5"
          />
        </SFrame>
      </FormProvider>
      <SFrame>
        <STable
          keyName="juchukoseigyoNo"
          data={rows}
          columns={columns}
          totalCount={rowCount}
          tableRef={tableRef}
          width={1000}
          pageSize={20}
          handleDoubleClick={
            isReference ? undefined : handleDoubleClickTableRow
          }
          rowSelectable={meiMethods.watch("meiTab")}
        />
      </SFrame>
      {isReference ? (
        <></>
      ) : (
        <FormProvider {...meiMethods}>
          <SFrame gridTemplateColumns="10rem 5rem 6rem 8rem 3rem 1rem 3rem 8rem 6rem 6rem 6rem">
            {!isReference && (
              <SEntryTab
                name="meiTab"
                gridColumn="span 3"
                handleOk={handleChangeMeiTab}
              />
            )}
            <SLabel required gridColumn="1">
              商品コード
            </SLabel>
            <SLabel required gridColumn="span 2">
              倉庫
            </SLabel>
            <SLabel>相手コード</SLabel>
            <SLabel gridColumn="span 3">WH(外)</SLabel>
            <SLabel>必要数</SLabel>
            <SLabel>可能数</SLabel>
            <SLabel>上代</SLabel>
            <SLabel required>掛率</SLabel>
            <SNumberField
              label="商品コード"
              name="shohinCd"
              required
              code
              size={10}
              {...register(4)}
              onBlur={handleChangeShohinCd}
              dialog={<B000200 />}
              handleSubmit={(data: any) => {
                meiMethods.setValue("shohinCd", data.shohinCd);
                meiMethods.setValue("janCd", data.janCd);
                meiMethods.setValue("shohinNm", data.shohinNm);
                (async () => {
                  await B010012Shohin(methods, meiMethods, navigate, setMsg);
                  reRendering();
                })();
              }}
              gridColumn="1"
            />
            <SNumberField
              label="倉庫コード"
              name="sokoCd"
              required
              code
              size={3}
              {...register(5)}
              onBlur={handleChangeSokoCd}
              dialog={<B000190 />}
              handleSubmit={(data: any) => {
                meiMethods.setValue("sokoCd", data?.sokoCd);
                meiMethods.setValue("sokoNm", data?.sokoNm);
                meiMethods.setValue("sokoshubetsuKbn", data?.sokoshubetsuKbn);
                (async () => {
                  await B010010Kanosu(meiMethods, navigate, setMsg);
                  reRendering();
                })();
              }}
            />
            <STextField name="sokoNm" size={40} disabled />
            <SNumberField name="aiteCd" code size={10} {...register(7)} />
            <SNumberField
              name="sotosunW"
              size={4}
              {...register(8)}
              onBlur={handleChangeWhTanka}
            />
            <SText>✕</SText>
            <SNumberField
              name="sotosunH"
              size={4}
              {...register(9)}
              onBlur={handleChangeWhTanka}
            />
            <SNumberField name="hitsuyosu" code size={6} disabled />
            <SNumberField name="kanosu" size={6} disabled />
            <SNumberField
              name="jodaitanka"
              size={9}
              {...register(13)}
              onBlur={handleCalcJuchutankaJuchuKin}
            />
            <SNumberField
              label="掛率"
              name="kakeritsu"
              required
              size={6}
              {...register(15)}
              onBlur={handleCalcJuchutankaJuchuKin}
            />
            <SLabel gridColumn="1">JANコード</SLabel>
            <SLabel gridColumn="span 3">商品名</SLabel>
            <SLabel gridColumn="span 3">WH(内)</SLabel>
            <SLabel required>構成数</SLabel>
            <SLabel>VOL</SLabel>
            <SLabel required>下代</SLabel>
            <SLabel required>金額</SLabel>
            <SNumberField name="janCd" code size={13} disabled gridColumn="1" />
            <STextField
              name="shohinNm"
              size={40}
              {...register(6)}
              disabled={!isShohinShokuchiKbn()}
              gridColumn="span 3"
            />
            <SNumberField name="uchisunW" size={4} {...register(10)} />
            <SText>✕</SText>
            <SNumberField name="uchisunH" size={4} {...register(11)} />
            {/* 受注生産品ORセット構成品の場合、在庫照会helpを非表示にする */}
            {isMeiJuchuSeisanhin() || isSetshohinKbn() ? (
              <SNumberField
                label="構成数"
                name="koseisu"
                required
                size={6}
                {...register(12)}
                onBlur={handleChangeKoseisu}
              />
            ) : (
              <SNumberField
                label="構成数"
                name="koseisu"
                required
                size={6}
                {...register(12)}
                onBlur={handleChangeKoseisu}
                dialog={<B000220 shohinCd={meiMethods.watch("shohinCd")} />}
                handleSubmit={(data: any) => {
                  meiMethods.setValue("shohinCd", data.shohinCd);
                  meiMethods.setValue("janCd", data.janCd);
                  meiMethods.setValue("shohinNm", data.shohinNm);
                  meiMethods.setValue("sokoCd", data.sokoCd);
                  meiMethods.setValue("sokoNm", data.sokoNm);
                  meiMethods.setValue("kanosu", data.hikiatekanosu);
                  meiMethods.setValue("sokoshubetsuKbn", data.sokoshubetsuKbn);
                  // 商品関連項目設定
                  (async () => {
                    await B010012ShohinZaiko(
                      methods,
                      meiMethods,
                      navigate,
                      setMsg
                    );
                    reRendering();
                  })();
                }}
              />
            )}
            <SNumberField name="vol" code size={10} disabled />
            <SNumberField
              label="下代"
              name="juchutanka"
              required
              size={9}
              {...register(14)}
              onBlur={handleCalcJuchuKinFromJuchutanka}
            />
            <SNumberField
              label="金額"
              name="juchuKin"
              required
              size={9}
              {...register(16)}
              disabled={isKingakuDisabled()}
            />
            <SText gridColumn="1"></SText>
            <SText gridColumn="span 7"></SText>
            <SLabel gridColumn="span 3">構成明細備考</SLabel>
            <SNumberField
              name="meisaikoseiBikoCd"
              code
              size={3}
              {...register(17)}
              onBlur={() =>
                B000111meisaikoseiBiko(meiMethods, navigate, setMsg)
              }
              dialog={<B000111 meishoKbn="7" />}
              handleSubmit={(data: any) => {
                meiMethods.setValue("meisaikoseiBikoCd", data?.meishoCd);
                meiMethods.setValue("meisaikoseiBikoBefore", data?.meishoCd);
                meiMethods.setValue("meisaikoseiBiko", data?.meishoNm1);
              }}
              gridColumn="9"
            />
            <STextField
              name="meisaikoseiBiko"
              size={20}
              {...register(18)}
              gridColumn="span 2"
            />
            {!meiMethods.watch("meiTab") && (
              <SCancelButton
                label={labels.buttons.deleteDetail}
                gridRow="8"
                gridColumn="1"
                onClick={handleClickDeleteMeiButton}
              />
            )}
            {meiMethods.watch("meiTab") ? (
              <SNormalButton
                label={labels.buttons.createDetail}
                {...register(19)}
                gridRow="8"
                gridColumn="11"
                align="right"
                onClick={handleClickCreateMeiButton}
              />
            ) : (
              <SNormalButton
                label={labels.buttons.updateDetail}
                {...register(19)}
                gridRow="8"
                gridColumn="11"
                align="right"
                onClick={handleClickUpdateMeiButton}
              />
            )}
          </SFrame>
        </FormProvider>
      )}
    </>
  );
};
